import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { ApiResponse } from "@/services/api/types";
import type { RequestReportResponse } from "@/services/features/AnalyticsService";

interface SessionAnalyticsParams {
  storeIds: number[];
  deviceType?: string | null;
  requestType?: string;
  startDate: string;
  endDate: string;
  requestStatus?: string;
  areaIds?: number[];
}

export interface SessionMetrics {
  totalSessions: number;
  totalSessionsIncludingNoItems: number;
  totalItems: number;
  totalSessionValue: number;
  date: string;
  numberOfNamedShoppers: number;
  totalDwellMillisecond: number;
  totalDwellMillisecondIncludingNoItems: number;
}

export interface RequestMetrics {
  totalRequests: number;
  totalMissedRequests: number;
  totalShoppers: number;
  totalRequestValue: number;
  totalTimeTakenSeconds: number;
}

export interface DailyRequestMetrics extends RequestMetrics {
  date: string;
}

export interface AreaSessionAnalyticsParams {
  storeId: number;
  startDate: string;
  endDate: string;
  roomType?: string;
}

export interface AreaSessionRoomMetrics {
  changingRoomId: number;
  changingRoomName: string | null;
  roomType: string;
  totalSessions: number;
  totalSessionsIncludingNoItems: number;
}

export interface AreaSessionMetrics {
  areaId: number;
  areaName: string | null;
  totalSessions: number;
  totalSessionsIncludingNoItems: number;
  rooms: AreaSessionRoomMetrics[];
}

export interface PopularItemsParams {
  storeId?: number;
  startDate: string;
  endDate: string;
  limit?: number;
  areaIds?: number[];
}

export interface PopularSku {
  sku: string;
  count: number;
}

export interface PopularProductsReportParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
  deviceType?: string;
  limit?: number;
}

export interface PopularProduct {
  productId: string | null;
  title: string | null;
  category?: string | null;
  count: number;
}

export interface ConvertedProductSummaryParams {
  storeIds: number[];
  productId?: string;
  deviceType?: string;
  startDate: string;
  endDate: string;
}

export interface ConvertedProductSummary {
  sku: string | null;
  totalPrice: number;
  numberOfItemsSold: number;
}

// Weekly session stats
export interface WeekSessionParams {
  storeIds: number[];
  deviceType?: string;
  startDate: string; // yyyy-MM-dd
  endDate?: string; // yyyy-MM-dd (optional)
}

export interface AggregateData<T> {
  storeId: number;
  storeName: string;
  storeGroup: string;
  customerId: number;
  customerName: string;
  start: string; // yyyy-MM-dd
  end: string; // yyyy-MM-dd
  data: T;
}

// Sales summary types
export interface SalesSummaryParams {
  storeIds: number[]; // maps to repeated `storeId`
  roomType?: string;
  deviceType?: string | null;
  startDate: string; // format yyyy-MM-dd
  endDate: string; // format yyyy-MM-dd
  areaIds?: number[];
}

export interface SalesStats {
  numberOfConvertedShoppers: number;
  numberOfItemsSold: number;
  totalBasketValue: number;
  latestTime: string; // server returns a date; keep as string
}

// Shopper feedback types
export interface ShopperFeedbackParams {
  storeIds: number[]; // maps to repeated `storeId`
  startDate?: string; // optional yyyy-MM-dd
  endDate?: string; // optional yyyy-MM-dd
}

export interface ShopperFeedbackAverage {
  numberOfShopperRating: number;
  totalShopperRating: number;
  numberOfDeviceRating: number;
  totalDeviceRating: number;
  latestTime: string; // server returns a date; keep as string
}

// Foot traffic types
export interface FootTrafficParams {
  storeIds: number[]; // maps to repeated `storeId`
  startDate?: string; // optional yyyy-MM-dd
  endDate?: string; // optional yyyy-MM-dd
}

export interface FootTrafficCount {
  trafficCount: number;
  latestTime: string; // server returns a date; keep as string
}

export interface DailyFootTrafficPoint {
  storeId: number;
  storeName: string;
  localCreatedAtDate: string;
  count: number;
}

export interface DailySalesDataPoint {
  storeId: number;
  storeName: string;
  localCreatedAtDate: string;
  totalPrice: number;
  totalCarts: number;
  numberOfItemsSold: number;
}

export type AlertStatus =
  "CREATED" | "ACCEPTED" | "ALL_CLEAR" | "MISSED" | "CONFIRMED" | "UNABLE_TO_ASSESS";

type SuspiciousActivityType = string;

interface SuspiciousActivity {
  id: number;
  type: SuspiciousActivityType;
  createdAt?: Date | string | number;
  changingRoomId?: number;
  note?: string | null;
}

export interface SuspiciousActivityAlert {
  id: number;
  changingRoomId: number;
  shopperId?: number;
  storeId: number;
  assignedUserId?: number | null;
  status: AlertStatus;
  createdAt: Date | string | number;
  completedAt?: Date | string | number | null;
  suspiciousActivities: SuspiciousActivity[];
  type?: "SUSPICIOUS_ACTIVITY_ALERT";
}

// Session stats by store types
export interface SessionByStoreParams {
  storeIds: number[]; // repeated as `storeId`
  deviceType?: string;
  startDate?: string; // optional yyyy-MM-dd
  endDate?: string; // optional yyyy-MM-dd
}

// Request stats by store types
export interface RequestByStoreParams {
  storeIds: number[]; // repeated as `storeId`
  deviceType?: string;
  startDate?: string; // optional yyyy-MM-dd
  endDate?: string; // optional yyyy-MM-dd
  requestType?: string;
  requestStatus?: string;
}

// Shopper feedback by store types
export interface ShopperFeedbackByStoreParams {
  storeIds: number[]; // repeated as `storeId`
  startDate?: string; // optional yyyy-MM-dd
  endDate?: string; // optional yyyy-MM-dd
}

export interface DailySeriesParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
  deviceType?: string;
  requestType?: string;
}

export interface ShopperDetailsParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
}

interface ShopperDetailsItem {
  id: number;
  shopperArchiveId: number;
  sku: string | null;
  price: number | null;
  state: string | null;
  createdAt: string | null;
  localCreatedAt: string | null;
  itemId: string | null;
  productId: string | null;
  serial: string | null;
  title: string | null;
  size: string | null;
  category: string | null;
  color: string | null;
  epc: string | null;
  size2: string | null;
  rfidStatus: string | null;
}

interface ShopperDetailsRequest {
  id: number;
  shopperArchiveId: number;
  requestId: number | null;
  status: string | null;
  type: string | null;
  title: string | null;
  size: string | null;
  sku: string | null;
  color: string | null;
  createdAt: string | null;
  localCreatedAt: string | null;
  fulfilledBy: string | null;
  assignedTo: string | null;
}

interface ShopperDetailsCartItem {
  id: number;
  cartId: string | null;
  storeId: number | null;
  customerId: number | null;
  createdAt: string | null;
  localCreatedAt: string | null;
  quantity: number | null;
  skuId: string | null;
  price: number | null;
  tag: string | null;
  itemId: string | null;
  productId: string | null;
}

interface ShopperDetails {
  shopperId: number;
  shopperName: string | null;
  engaged: boolean | null;
  storeId: number;
  storeName: string | null;
  customerId: number;
  customerName: string | null;
  changingRoomType: string | null;
  shopperLocalCreatedAt: string | null;
  madeRequest: boolean | null;
  madeItemRequest: boolean | null;
  converted: boolean | null;
  conversionType: string | null;
  numberOfItems: number | null;
  valueOfItems: number | null;
  numberOfItemsInCart: number | null;
  valueOfItemsInCart: number | null;
  items: ShopperDetailsItem[];
  requests: ShopperDetailsRequest[];
  cart: ShopperDetailsCartItem[];
}

export interface ShopperDetailsResponse {
  shopper: ShopperDetails;
}

export type ReportExportStatus = "PENDING" | "RUNNING" | "COMPLETE" | "FAILED" | "EXPIRED";

export interface ConversionExportParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
}

export interface ReportExport {
  id: string;
  status: ReportExportStatus;
  rowCount: number | null;
  fileSizeBytes: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
}

export interface OutOfStockReportParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
}

export interface FrequentOutOfStock {
  storeId: number | null;
  customerId: number | null;
  productId: string | null;
  color: string | null;
  size: string | null;
  size2: string | null;
  count: number;
  productTotalCount: number;
}

export type ChangingRoomSuspectItemSourceType = "CENTRAL_RFID" | "CHANGING_ROOM";

export interface ContinuousReadsReportParams {
  storeIds: number[];
  startDate: string;
  endDate: string;
  sourceType?: ChangingRoomSuspectItemSourceType;
}

export interface ChangingRoomSuspectItem {
  id: number;
  storeId: number;
  changingRoomId: number;
  sourceType: ChangingRoomSuspectItemSourceType;
  epc: string;
  skuId?: string | null;
  firstSeenAt: Date | string | number;
  lastSeenAt: Date | string | number;
  createdAt?: Date | string | number | null;
}

export class AnalyticsServiceV2 extends BaseApiService {
  private readonly requestCache = new Map<
    string,
    { expiresAt: number; response: Promise<ApiResponse<unknown>> }
  >();
  private static instance: AnalyticsServiceV2;

  private constructor() {
    super();
  }

  public static getInstance(): AnalyticsServiceV2 {
    if (!AnalyticsServiceV2.instance) {
      AnalyticsServiceV2.instance = new AnalyticsServiceV2();
    }
    return AnalyticsServiceV2.instance;
  }

  private getAnalyticsBaseUrl(): string {
    return getApiConfig().analytics;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  /**
   * Coalesce identical analytics requests issued by cards on the same page.
   * The short retention period also covers React Strict Mode's development-only
   * effect replay without turning this service into a long-lived data cache.
   */
  private getCachedAnalyticsRequest<T>(
    endpoint: string,
    params: SessionAnalyticsParams,
  ): Promise<ApiResponse<T>> {
    const key = JSON.stringify({
      endpoint,
      storeIds: [...params.storeIds].sort((first, second) => first - second),
      deviceType: params.deviceType ?? null,
      requestType: params.requestType ?? null,
      requestStatus: params.requestStatus ?? null,
      areaIds: params.areaIds ?? null,
      startDate: params.startDate,
      endDate: params.endDate,
    });
    return this.getCachedAnalyticsResponse(key, () =>
      this.makeAnalyticsRequest<T>(endpoint, params),
    );
  }

  private getCachedAnalyticsResponse<T>(
    key: string,
    fetchResponse: () => Promise<ApiResponse<T>>,
  ): Promise<ApiResponse<T>> {
    const cachedRequest = this.requestCache.get(key);

    if (cachedRequest && cachedRequest.expiresAt > Date.now()) {
      return cachedRequest.response as Promise<ApiResponse<T>>;
    }

    const response = fetchResponse();
    this.requestCache.set(key, {
      expiresAt: Date.now() + 2_000,
      response: response as Promise<ApiResponse<unknown>>,
    });
    void response.catch(() => this.requestCache.delete(key));

    return response;
  }

  private async makeAnalyticsRequest<T>(
    endpoint: string,
    params: SessionAnalyticsParams,
  ): Promise<ApiResponse<T>> {
    const queryParams = new URLSearchParams();

    for (const storeId of params.storeIds) {
      queryParams.append("storeId", storeId.toString());
    }

    if (params.deviceType) {
      queryParams.append("deviceType", params.deviceType);
    }

    if (params.requestType) {
      queryParams.append("requestType", params.requestType);
    }

    if (params.requestStatus) {
      queryParams.append("requestStatus", params.requestStatus);
    }

    for (const areaId of params.areaIds ?? []) {
      queryParams.append("areaId", areaId.toString());
    }

    queryParams.append("startDate", params.startDate);
    queryParams.append("endDate", params.endDate);

    const queryString = queryParams.toString();
    const url = `${endpoint}${queryString ? "?" + queryString : ""}`;

    return await this.get<any>(url, this.getAnalyticsBaseUrl());
  }

  private buildStoreDateQueryParams(params: OutOfStockReportParams): URLSearchParams {
    const queryParams = new URLSearchParams();

    for (const id of params.storeIds) {
      queryParams.append("storeId", id.toString());
    }

    queryParams.append("startDate", params.startDate);
    queryParams.append("endDate", params.endDate);

    return queryParams;
  }

  public async getSessionAnalytics(
    params: SessionAnalyticsParams,
  ): Promise<ApiResponse<SessionMetrics>> {
    return this.getCachedAnalyticsRequest<SessionMetrics>("/analytic/session", params);
  }

  public async getRequestAnalytics(
    params: SessionAnalyticsParams,
  ): Promise<ApiResponse<RequestMetrics>> {
    return this.getCachedAnalyticsRequest<RequestMetrics>("/analytic/request", params);
  }

  public async getDailySessionAnalytics(
    params: DailySeriesParams,
  ): Promise<ApiResponse<SessionMetrics[]>> {
    return this.getCachedAnalyticsRequest<SessionMetrics[]>("/analytic/chart/session", params);
  }

  public async getDailyRequestAnalytics(
    params: DailySeriesParams,
  ): Promise<ApiResponse<DailyRequestMetrics[]>> {
    return this.getCachedAnalyticsRequest<DailyRequestMetrics[]>("/analytic/chart/request", params);
  }

  public async getAreaSessionAnalytics(
    params: AreaSessionAnalyticsParams,
  ): Promise<ApiResponse<AreaSessionMetrics[]>> {
    const queryParams = new URLSearchParams({
      storeId: params.storeId.toString(),
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.roomType) queryParams.set("roomType", params.roomType);

    const endpoint = `/analytic/session/by-area?${queryParams.toString()}`;
    const key = JSON.stringify({ endpoint, ...params });

    return this.getCachedAnalyticsResponse(key, () =>
      this.get<ApiResponse<AreaSessionMetrics[]>>(endpoint, this.getAnalyticsBaseUrl()),
    );
  }

  public async getRequestsByStatus(
    storeIds: number[],
    status: string,
    startDate: string,
    endDate: string,
  ): Promise<RequestReportResponse> {
    const queryParams = new URLSearchParams();

    for (const storeId of storeIds) {
      queryParams.append("storeIds", storeId.toString());
    }

    queryParams.append("startDate", startDate);
    queryParams.append("endDate", endDate);

    return await this.get<RequestReportResponse>(
      `/v2/report/request/${status}/days?${queryParams.toString()}`,
      this.getCoreBaseUrl(),
    );
  }

  public async getSuspiciousActivityAlerts(
    storeIds: number[],
    startDate: string,
    endDate: string,
    deviceType?: string,
  ): Promise<ApiResponse<SuspiciousActivityAlert[]>> {
    const queryParams = new URLSearchParams();

    for (const storeId of storeIds) {
      queryParams.append("storeIds", storeId.toString());
    }

    queryParams.append("startDate", startDate);
    queryParams.append("endDate", endDate);
    if (deviceType) {
      queryParams.append("deviceType", deviceType);
    }

    return await this.get<ApiResponse<SuspiciousActivityAlert[]>>(
      `/v2/report/suspicious-activity/days?${queryParams.toString()}`,
      this.getCoreBaseUrl(),
    );
  }

  public async getContinuousReadsReport(
    params: ContinuousReadsReportParams,
  ): Promise<ApiResponse<ChangingRoomSuspectItem[]>> {
    const queryParams = new URLSearchParams();

    for (const storeId of params.storeIds) {
      queryParams.append("storeIds", storeId.toString());
    }

    queryParams.append("startDate", params.startDate);
    queryParams.append("endDate", params.endDate);

    if (params.sourceType) {
      queryParams.append("sourceType", params.sourceType);
    }

    return await this.get<ApiResponse<ChangingRoomSuspectItem[]>>(
      `/v2/report/stuck-tag/days?${queryParams.toString()}`,
      this.getCoreBaseUrl(),
    );
  }

  public async popularSkus(params: PopularItemsParams): Promise<ApiResponse<PopularSku[]>> {
    const queryParams = new URLSearchParams();

    if (params.storeId) {
      queryParams.append("storeId", params.storeId.toString());
    }

    queryParams.append("startDate", params.startDate);
    queryParams.append("endDate", params.endDate);

    if (params.limit) {
      queryParams.append("limit", params.limit.toString());
    }

    for (const areaId of params.areaIds ?? []) {
      queryParams.append("areaId", areaId.toString());
    }

    return await this.get<ApiResponse<PopularSku[]>>(
      `/analytic/popular-skus?${queryParams}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getPopularItems(params: PopularItemsParams): Promise<ApiResponse<PopularSku[]>> {
    return this.popularSkus(params);
  }

  public async getPopularProducts(
    params: PopularProductsReportParams,
  ): Promise<ApiResponse<PopularProduct[]>> {
    const queryParams = this.buildStoreDateQueryParams(params);

    if (params.deviceType) {
      queryParams.append("deviceType", params.deviceType);
    }

    if (params.limit) {
      queryParams.append("limit", params.limit.toString());
    }

    return await this.get<ApiResponse<PopularProduct[]>>(
      `/analytic/popular-products?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getConvertedProductSummary(
    params: ConvertedProductSummaryParams,
  ): Promise<ApiResponse<ConvertedProductSummary>> {
    const queryParams = this.buildStoreDateQueryParams(params);

    if (params.productId) {
      queryParams.append("productId", params.productId);
    }

    if (params.deviceType) {
      queryParams.append("deviceType", params.deviceType);
    }

    return await this.get<ApiResponse<ConvertedProductSummary>>(
      `/analytic/converted-product-summary?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getSalesSummary(params: SalesSummaryParams): Promise<ApiResponse<SalesStats>> {
    return this.getCachedAnalyticsRequest<SalesStats>(
      "/analytic/sales-summary/converted-shoppers",
      params,
    );
  }

  public async getShopperDetails(
    params: ShopperDetailsParams,
  ): Promise<ApiResponse<ShopperDetailsResponse[]>> {
    const queryParams = new URLSearchParams();

    for (const id of params.storeIds) {
      queryParams.append("storeId", id.toString());
    }

    queryParams.append("startDate", params.startDate);
    queryParams.append("endDate", params.endDate);

    return await this.get<ApiResponse<ShopperDetailsResponse[]>>(
      `/analytic/shoppers?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async createConversionExport(
    params: ConversionExportParams,
  ): Promise<ApiResponse<ReportExport>> {
    return await this.post<ApiResponse<ReportExport>>(
      "/exports/conversions",
      params,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getReportExport(exportId: string): Promise<ApiResponse<ReportExport>> {
    return await this.get<ApiResponse<ReportExport>>(
      `/exports/${encodeURIComponent(exportId)}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async downloadReportExport(exportId: string): Promise<Blob> {
    return await this.getBlob(
      `/exports/${encodeURIComponent(exportId)}/download`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getOutOfStockCount(params: OutOfStockReportParams): Promise<ApiResponse<number>> {
    const queryParams = this.buildStoreDateQueryParams(params);

    return await this.get<ApiResponse<number>>(
      `/analytic/out-of-stock-count?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getOutOfStockRecoveryCount(
    params: OutOfStockReportParams,
  ): Promise<ApiResponse<number>> {
    const queryParams = this.buildStoreDateQueryParams(params);

    return await this.get<ApiResponse<number>>(
      `/analytic/out-of-stock-recovery-count?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getFrequentOutOfStocks(
    params: OutOfStockReportParams,
  ): Promise<ApiResponse<FrequentOutOfStock[]>> {
    const queryParams = this.buildStoreDateQueryParams(params);

    return await this.get<ApiResponse<FrequentOutOfStock[]>>(
      `/analytic/frequent-out-of-stocks?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getShopperFeedbackAverage(
    params: ShopperFeedbackParams,
  ): Promise<ApiResponse<ShopperFeedbackAverage>> {
    const queryParams = new URLSearchParams();

    for (const id of params.storeIds || []) {
      queryParams.append("storeId", id.toString());
    }

    if (params.startDate) {
      queryParams.append("startDate", params.startDate);
    }

    if (params.endDate) {
      queryParams.append("endDate", params.endDate);
    }

    const qs = queryParams.toString();
    const endpoint = `/analytic/shopper-feedback?${qs}`;
    return this.getCachedAnalyticsResponse(endpoint, () =>
      this.get<ApiResponse<ShopperFeedbackAverage>>(endpoint, this.getAnalyticsBaseUrl()),
    );
  }

  public async getFootTrafficCount(
    params: FootTrafficParams,
  ): Promise<ApiResponse<FootTrafficCount>> {
    const queryParams = new URLSearchParams();

    for (const id of params.storeIds || []) {
      queryParams.append("storeId", id.toString());
    }

    if (params.startDate) {
      queryParams.append("startDate", params.startDate);
    }

    if (params.endDate) {
      queryParams.append("endDate", params.endDate);
    }

    const qs = queryParams.toString();
    return await this.get<ApiResponse<FootTrafficCount>>(
      `/analytic/foot-traffic?${qs}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  // GET /analytic/week/session -> Map<Long, List<AggregateData<SessionStats>>>
  public async getSessionStatsByWeek(
    params: WeekSessionParams,
  ): Promise<ApiResponse<Record<string, AggregateData<SessionMetrics>[]>>> {
    const query = new URLSearchParams();

    for (const id of [...params.storeIds].sort((first, second) => first - second)) {
      query.append("storeId", id.toString());
    }

    if (params.deviceType) {
      query.append("deviceType", params.deviceType);
    }

    query.append("startDate", params.startDate);

    if (params.endDate) {
      query.append("endDate", params.endDate);
    }

    const qs = query.toString();
    return await this.get<ApiResponse<Record<string, AggregateData<SessionMetrics>[]>>>(
      `/analytic/week/session?${qs}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  // GET /analytic/session/by-store -> Map<Long, SessionStats>
  public async getSessionsStatsByStore(
    params: SessionByStoreParams,
  ): Promise<ApiResponse<Record<string, SessionMetrics>>> {
    const query = new URLSearchParams();

    for (const id of params.storeIds) {
      query.append("storeId", id.toString());
    }

    if (params.deviceType) {
      query.append("deviceType", params.deviceType);
    }

    if (params.startDate) {
      query.append("startDate", params.startDate);
    }

    if (params.endDate) {
      query.append("endDate", params.endDate);
    }

    const qs = query.toString();
    const endpoint = `/analytic/session/by-store?${qs}`;
    return this.getCachedAnalyticsResponse(endpoint, () =>
      this.get<ApiResponse<Record<string, SessionMetrics>>>(endpoint, this.getAnalyticsBaseUrl()),
    );
  }

  // GET /analytic/request/by-store -> Map<Long, RequestStats>
  public async getRequestStatsByStore(
    params: RequestByStoreParams,
  ): Promise<ApiResponse<Record<string, RequestMetrics>>> {
    const query = new URLSearchParams();

    for (const id of [...params.storeIds].sort((first, second) => first - second)) {
      query.append("storeId", id.toString());
    }

    if (params.deviceType) {
      query.append("deviceType", params.deviceType);
    }

    if (params.startDate) {
      query.append("startDate", params.startDate);
    }

    if (params.endDate) {
      query.append("endDate", params.endDate);
    }

    if (params.requestType) {
      query.append("requestType", params.requestType);
    }

    if (params.requestStatus) {
      query.append("requestStatus", params.requestStatus);
    }

    const qs = query.toString();
    const endpoint = `/analytic/request/by-store?${qs}`;
    return this.getCachedAnalyticsResponse(endpoint, () =>
      this.get<ApiResponse<Record<string, RequestMetrics>>>(endpoint, this.getAnalyticsBaseUrl()),
    );
  }

  // GET /analytic/shopper-feedback/by-store -> Map<Long, ShopperFeedbackAverage>
  public async getShopperFeedbackAverageByStore(
    params: ShopperFeedbackByStoreParams,
  ): Promise<ApiResponse<Record<string, ShopperFeedbackAverage>>> {
    const query = new URLSearchParams();

    for (const id of [...params.storeIds].sort((first, second) => first - second)) {
      query.append("storeId", id.toString());
    }

    if (params.startDate) {
      query.append("startDate", params.startDate);
    }

    if (params.endDate) {
      query.append("endDate", params.endDate);
    }

    const qs = query.toString();
    const endpoint = `/analytic/shopper-feedback/by-store?${qs}`;
    return this.getCachedAnalyticsResponse(endpoint, () =>
      this.get<ApiResponse<Record<string, ShopperFeedbackAverage>>>(
        endpoint,
        this.getAnalyticsBaseUrl(),
      ),
    );
  }

  private parseToDate(value: string | null): Date | null {
    if (!value) return null;

    const ts = Date.parse(`${value}T00:00:00`);
    if (Number.isNaN(ts)) return null;

    return new Date(ts);
  }

  public async getLatestFootTrafficDateForStore(storeId: number): Promise<Date | null> {
    const queryParams = new URLSearchParams();
    queryParams.append("storeId", storeId.toString());
    const resp = await this.get<import("../api/types").ApiResponse<string>>(
      `/analytic/latest-foot-traffic-date?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
    return this.parseToDate(resp?.data);
  }

  public async getLatestSalesDateForStore(storeId: number): Promise<Date | null> {
    const queryParams = new URLSearchParams();
    queryParams.append("storeId", storeId.toString());
    const resp = await this.get<import("../api/types").ApiResponse<string>>(
      `/analytic/latest-sales-date?${queryParams.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
    return this.parseToDate(resp?.data);
  }

  public async getDailyFootTraffic(
    params: DailySeriesParams,
  ): Promise<ApiResponse<DailyFootTrafficPoint[]>> {
    const query = new URLSearchParams();

    for (const id of params.storeIds) {
      query.append("storeId", id.toString());
    }

    query.append("startDate", params.startDate);
    query.append("endDate", params.endDate);

    return await this.get<ApiResponse<DailyFootTrafficPoint[]>>(
      `/analytic/chart/foot-traffic?${query.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }

  public async getDailySalesData(
    params: DailySeriesParams,
  ): Promise<ApiResponse<DailySalesDataPoint[]>> {
    const query = new URLSearchParams();

    for (const id of params.storeIds) {
      query.append("storeId", id.toString());
    }

    query.append("startDate", params.startDate);
    query.append("endDate", params.endDate);

    return await this.get<ApiResponse<DailySalesDataPoint[]>>(
      `/analytic/chart/sales?${query.toString()}`,
      this.getAnalyticsBaseUrl(),
    );
  }
}
